import React, { useState, useMemo } from 'react';
import ReactMarkdown from 'react-markdown';

export default function FilterTable({ data, tab, columns }) {
  const [filters, setFilters] = useState({});
  const [modalCol, setModalCol] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedValues, setSelectedValues] = useState({});
  const [selectAll, setSelectAll] = useState(true);

  const [sortConfig, setSortConfig] = useState({
    col: columns[0],
    direction: 'asc',
  });
  
  const getUniqueValues = (col) => [...new Set(data.map((row) => row[col]))];

  const filteredData = data.filter((row) =>
    Object.entries(filters).every(([col, values]) =>
      values.length === 0 ? true : values.includes(row[col])
    )
  );
 
  const sortedData = useMemo(() => {
    let dataToSort = [...filteredData];

    const sorted = [...filteredData].sort((a, b) => {
      const valA = a[sortConfig.col];
      const valB = b[sortConfig.col];
  
      if (sortConfig.col === 'type') {
        const isEmptyA = !valA?.trim();
        const isEmptyB = !valB?.trim();
  
        if (isEmptyA && !isEmptyB) return 1;
        if (!isEmptyA && isEmptyB) return -1;
      }

       if (sortConfig.col === 'Asset') {
        const isEmptyA = !valA?.trim();
        const isEmptyB = !valB?.trim();
  
        if (isEmptyA && !isEmptyB) return 1;
        if (!isEmptyA && isEmptyB) return -1;
      }
  
      if (typeof valA === 'number' && typeof valB === 'number') {
        return sortConfig.direction === 'asc' ? valA - valB : valB - valA;
      }
  
      return sortConfig.direction === 'asc'
        ? String(valA).localeCompare(String(valB))
        : String(valB).localeCompare(String(valA));
    });

    return [...sorted].sort((a, b) => {
      const valA = a.reasonForEarlyClosure;
      const valB = b.reasonForEarlyClosure;
  
      const isEmptyA = valA === null || valA === undefined || valA.trim?.() === '';
      const isEmptyB = valB === null || valB === undefined || valB.trim?.() === '';
  
      if (isEmptyA && !isEmptyB) return 1;
      if (!isEmptyA && isEmptyB) return -1;
  
      return 0; // don't change order otherwise
    });
  }, [filteredData, sortConfig]);
  

  const toggleSort = (col) => {
    setSortConfig((prev) => {
      if (prev.col === col) {
        if (prev.direction === 'asc') return { col, direction: 'desc' };
        if (prev.direction === 'desc') return { col: null, direction: null };
      }
      return { col, direction: 'asc' };
    });
  };

  const openModal = (col) => {
    const unique = getUniqueValues(col);
    setModalCol(col);
    setSearchTerm('');
    setSelectedValues((prev) => ({
      ...prev,
      [col]: filters[col]?.length ? filters[col] : unique,
    }));
    setSelectAll(filters[col]?.length === 0 || filters[col]?.length === unique.length);
  };

  const closeModal = () => {
    setModalCol(null);
    setSearchTerm('');
  };

 const filteredUniqueValues = modalCol
  ? getUniqueValues(modalCol).filter((v) =>
      v?.toString().toLowerCase().includes(searchTerm.toLowerCase())
    )
  : [];

  const onCheck = (val) => {
    let newSelected = selectedValues[modalCol] || [];
    if (newSelected.includes(val)) {
      newSelected = newSelected.filter((v) => v !== val);
    } else {
      newSelected = [...newSelected, val];
    }
    setSelectedValues({ ...selectedValues, [modalCol]: newSelected });

    const visible = filteredUniqueValues;
    setSelectAll(newSelected.filter((v) => visible.includes(v)).length === visible.length);
  };

  const onSelectAll = () => {
    if (selectAll) {
      setSelectedValues((prev) => ({ ...prev, [modalCol]: [] }));
      setSelectAll(false);
    } else {
      setSelectedValues((prev) => ({
        ...prev,
        [modalCol]: filteredUniqueValues,
      }));
      setSelectAll(true);
    }
  };

  const applyFilter = (col) => {
    const valuesToApply = selectedValues[col] || [];
    const visibleValues = filteredUniqueValues;
    const finalValues = valuesToApply.filter((val) => visibleValues.includes(val));

    setFilters({ [col]: finalValues });
    closeModal();
  };

  return (
    <div style={{ position: 'relative' }}>
      <div className='totalAssets'>Total {tab==='tab4'?'Asset Records':'Projects'}:{filteredData.length?' '+filteredData.length:' '+0}</div>    
      <table className={tab==='tab4'?"table-bordered smart-table projectMyTable":"table-bordered smart-table"} style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col}
                style={{
                  fontSize:'14px',
                  border: '1px solid #ccc',
                  padding: 10,
                  position: 'relative',
                  cursor: 'pointer',
                  width: 'fit-content'
                }}
              >
                <div
                  style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                >
                  <span onClick={() => toggleSort(col)}>
                    {col.toUpperCase()}
                    {sortConfig.col === col && (
                      <span style={{ marginLeft: 5 }}>
                        {sortConfig.direction === 'asc' ? '▲' : '▼'}
                      </span>
                    )}
                  </span>
                  <i className="bi bi-filter" style={{ width: "16px", height: "16px", cursor: "pointer" }} onClick={() => openModal(col)}></i>
            
                </div>
                {modalCol === col && (
                  <FilterModal
                    col={col}
                    searchTerm={searchTerm}
                    setSearchTerm={setSearchTerm}
                    filteredUniqueValues={filteredUniqueValues}
                    selectedValues={selectedValues}
                    setSelectedValues={setSelectedValues}
                    selectAll={selectAll}
                    setSelectAll={setSelectAll}
                    onCheck={onCheck}
                    onSelectAll={onSelectAll}
                    applyFilter={applyFilter}
                    closeModal={closeModal}
                  />
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {sortedData.length === 0 ? (
            <tr>
              <td colSpan={columns.length} style={{ textAlign: 'center', padding: 10 }}>
                No data
              </td>
            </tr>
          ) : (
            sortedData.map((row, idx) => (
              <tr key={idx}>
                {columns.map((col) => (
                  <td key={col} style={{ fontSize:'12px',border: '1px solid #ccc', padding: 10 }}>
                    <ReactMarkdown>{row[col]}</ReactMarkdown>
                  </td>
                ))}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Modal Component
function FilterModal({
  col,
  searchTerm,
  setSearchTerm,
  filteredUniqueValues,
  selectedValues,
  setSelectedValues,
  selectAll,
  setSelectAll,
  onCheck,
  onSelectAll,
  applyFilter,
  closeModal,
}) {
  console.log('filteredUniqueValues',filteredUniqueValues);
    if (!Array.isArray(filteredUniqueValues) || filteredUniqueValues.length === 0) {
    filteredUniqueValues=[];
  } else {
      filteredUniqueValues.sort((a, b) => {
      // both numbers
      if (!isNaN(a) && !isNaN(b)) return Number(a) - Number(b);
      // both strings
      return a.toString().localeCompare(b.toString());
    });
  }
  return (
    <div
      style={{
        position: 'absolute',
        top: '100%',
        left: 0,
        zIndex: 10,
        background: '#fff',
        border: '1px solid #ccc',
        padding: 10,
        minWidth: 200,
      }}
    >
      <input
        type="text"
        placeholder="Search..."
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ width: '100%', marginBottom: 10 }}
      />
      <div>
        <label>
          <input type="checkbox" checked={selectAll} onChange={onSelectAll} />
          Select All
        </label>
      </div>
      <div style={{ maxHeight: 150, overflowY: 'auto' }}>
        {filteredUniqueValues.map((val) => (
          <div key={val}>
            <label>
              <input
                type="checkbox"
                checked={selectedValues[col]?.includes(val) || false}
                onChange={() => onCheck(val)}
              />
              {val}
            </label>
          </div>
        ))}
      </div>
      <div style={{ marginTop: 10, display: 'flex', gap: 10 }}>
        <button className="btn btn-sm btn-primary" onClick={() => applyFilter(col)}>Apply</button>
        <button className="btn btn-sm btn-secondary" onClick={closeModal}>Close</button>
      </div>
    </div>
  );
}
